// WorkersTable.js
// Reusable workers table component.
import React from 'react';


const WorkersTable = ({
  userData,
  workers,
  styles,
  formatNumber,
  getExpectedHashrate,
  isDetailsTab
}) => {
  const sha256Active = userData?.hashrate?.sha256?.hashrate > 0;
  
  const filteredWorkers = workers.filter(w => w.algorithm === 'kheavyhash' || (w.algorithm === 'sha256' && w.hashrate > 0 && sha256Active));
  const showAlgorithm = sha256Active;
  if (filteredWorkers.length === 0) return null;
  return (
    <div className="box mt-5" style={styles.tableBox}>
      <h2 className="title is-5 mb-4" style={styles.tableTitle}>PowerPool Workers</h2>
      <div className="table-container">
        <table className="table is-fullwidth is-hoverable" style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Status</th>
              <th style={styles.tableHeader}>Name</th>
              {isDetailsTab && showAlgorithm && <th style={styles.tableHeader}>Algorithm</th>}
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Hashrate</th>
              <th style={{...styles.tableHeader, textAlign: 'right'}}>Avg Hashrate</th>
              {isDetailsTab && <th style={{...styles.tableHeader, textAlign: 'right'}}>Valid</th>}
              {isDetailsTab && <th style={{...styles.tableHeader, textAlign: 'right'}}>Invalid</th>}
              {isDetailsTab && <th style={{...styles.tableHeader, textAlign: 'right'}}>Rejected %</th>}
            </tr>
          </thead>
          <tbody>
            {filteredWorkers.map((worker, index) => (
              <tr key={index}>
                <td style={styles.tableCell}>
                  {(() => {
                    let hashrateTH = worker.hashrate;
                    if (worker.name.toLowerCase().includes('ks0') && worker.hashrate_units?.includes('GH')) {
                      hashrateTH = worker.hashrate / 1000;
                    }
                    const expected = getExpectedHashrate(worker.name);
                    let tagClass = 'tag ';
                    let hover = undefined;
                    if (worker.hashrate > 0) {
                      if (hashrateTH < 0.5 * expected) {
                        tagClass += 'is-warning';
                        hover = `Expected ${formatNumber(expected, 3)} TH/s`;
                      } else {
                        tagClass += 'is-success';
                      }
                    } else {
                      tagClass += 'is-danger';
                      hover = `Expected ${formatNumber(expected, 3)} TH/s`;
                    }
                    return hover ? (
                      <span className={tagClass} title={hover}>
                        {worker.hashrate > 0 ? 'Online' : 'Offline'}
                      </span>
                    ) : (
                      <span className={tagClass}>
                        {worker.hashrate > 0 ? 'Online' : 'Offline'}
                      </span>
                    );
                  })()}
                </td>
                <td style={styles.tableCell}>
                  {(() => {
                    const rawName = worker.name.replace('jjphotos.', '');
                    if (rawName.startsWith('ks0')) {
                      const suffix = rawName.slice(3);
                      let display = 'ks0';
                      if (suffix === 'Neville') display = 'ks02';
                      else if (suffix === 'Irwin') display = 'ks03';
                      else if (suffix === 'Ronaldo') display = 'ks07';
                      return <span title={suffix}>{display}</span>;
                    }
                    if (rawName === 'k5pro2') return 'ks5pro2';
                    return rawName;
                  })()}
                </td>
                {isDetailsTab && showAlgorithm && <td style={styles.tableCell}>{worker.algorithm}</td>}
                <td style={{...styles.tableCell, textAlign: 'right'}}>
                  {(() => {
                    let hashrateTH = worker.hashrate;
                    if (worker.name.toLowerCase().includes('ks0') && worker.hashrate_units?.includes('GH')) {
                      hashrateTH = worker.hashrate / 1000;
                    }
                    const expected = getExpectedHashrate(worker.name);
                    let color = '#22c55e';
                    let hover = undefined;
                    if (hashrateTH < 0.5 * expected) {
                      color = '#ef4444';
                      hover = `Expected ${formatNumber(expected, 1)} TH/s`;
                    } else if (hashrateTH < 0.85 * expected) {
                      color = '#f59e42';
                      hover = `Expected ${formatNumber(expected, 1)} TH/s`;
                    } else if (hashrateTH < expected) {
                      color = '#e2e8f0';
                    }
                    return hover ? (
                      <span style={{color}} title={hover}>
                        {formatNumber(worker.hashrate, 1)} {worker.hashrate_units}
                      </span>
                    ) : (
                      <span style={{color}}>
                        {formatNumber(worker.hashrate, 1)} {worker.hashrate_units}
                      </span>
                    );
                  })()}
                </td>
                <td style={{...styles.tableCell, textAlign: 'right'}}>
                  {(() => {
                    let hashrateAvgTH = worker.hashrate_avg;
                    if (worker.name.toLowerCase().includes('ks0') && worker.hashrate_avg_units?.includes('GH')) {
                      hashrateAvgTH = worker.hashrate_avg / 1000;
                    }
                    const expected = getExpectedHashrate(worker.name);
                    let color = '#22c55e';
                    if (hashrateAvgTH < 0.5 * expected) {
                      color = '#ef4444';
                    } else if (hashrateAvgTH < 0.85 * expected) {
                      color = '#f59e42';
                    } else if (hashrateAvgTH < expected) {
                      color = '#e2e8f0';
                    }
                    return (
                      <span style={{color}}>
                        {formatNumber(worker.hashrate_avg, 1)} {worker.hashrate_avg_units}
                      </span>
                    );
                  })()}
                </td>
                {isDetailsTab && (
                  <>
                    <td style={{...styles.tableCell, textAlign: 'right'}}>{formatNumber(worker.valid_shares, 0)}</td>
                    <td style={{...styles.tableCell, textAlign: 'right'}}>{formatNumber(worker.invalid_shares, 0)}</td>
                    <td style={{...styles.tableCell, textAlign: 'right'}}>
                      <span style={worker.rejectedPercentage > 5 ? {color: '#ef4444'} : {}}>
                        {formatNumber(worker.rejectedPercentage, 2)}%
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {/* Totals row */}
            <tr style={{ fontWeight: 'bold', background: '#1e293b', color: '#f59e42' }}>
              <td colSpan={isDetailsTab && showAlgorithm ? 3 : 2} style={styles.tableCell}>Totals</td>
              <td style={{...styles.tableCell, textAlign: 'right'}}>
                {formatNumber(filteredWorkers.reduce((sum, w) => {
                  let hashrateTH = w.hashrate;
                  if (w.name.toLowerCase().includes('ks0') && w.hashrate_units?.includes('GH')) {
                    hashrateTH = w.hashrate / 1000;
                  }
                  return sum + hashrateTH;
                }, 0), 1)} TH/s
              </td>
              <td style={{...styles.tableCell, textAlign: 'right'}}>
                {formatNumber(filteredWorkers.reduce((sum, w) => {
                  let hashrateAvgTH = w.hashrate_avg;
                  if (w.name.toLowerCase().includes('ks0') && w.hashrate_avg_units?.includes('GH')) {
                    hashrateAvgTH = w.hashrate_avg / 1000;
                  }
                  return sum + hashrateAvgTH;
                }, 0), 1)} TH/s
              </td>
              {isDetailsTab && <td colSpan={3} />}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkersTable;
